<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
    <div class="max-w-md w-full">
      <div class="bg-white rounded-lg shadow-lg p-8">
        <div class="text-center mb-8">
          <h1 class="text-3xl font-bold text-gray-800">Asya Pasifik Kütüphane</h1>
          <p class="text-gray-600 mt-2">Dosyalarınızı kolaylıkla yönetin</p>
        </div>

        <!-- Login Form -->
        <form v-if="!isRegisterMode" @submit.prevent="handleLogin" class="space-y-6">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
              E-posta Adresi
            </label>
            <input
              id="email"
              type="email"
              v-model="loginForm.email"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="E-postanızı girin"
            >
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 mb-2">
              Şifre
            </label>
            <input
              id="password"
              type="password"
              v-model="loginForm.password"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Şifrenizi girin"
            >
          </div>

          <button
            type="submit"
            :disabled="authStore.loading"
            class="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span v-if="authStore.loading" class="flex items-center justify-center">
              <i class="fas fa-spinner fa-spin mr-2"></i>
              Giriş yapılıyor...
            </span>
            <span v-else>Giriş Yap</span>
          </button>

          <!-- Forgot Password Link -->
          <div class="text-center">
            <router-link
              to="/forgot-password"
              class="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Şifrenizi mi unuttunuz?
            </router-link>
          </div>
        </form>

        <!-- Register Form -->
        <form v-else @submit.prevent="handleRegister" class="space-y-6">
          <div>
            <label for="reg-name" class="block text-sm font-medium text-gray-700 mb-2">
              Ad Soyad (İsteğe bağlı)
            </label>
            <input
              id="reg-name"
              type="text"
              v-model="registerForm.name"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Adınızı ve soyadınızı girin"
            >
          </div>

          <div>
            <label for="reg-email" class="block text-sm font-medium text-gray-700 mb-2">
              E-posta Adresi
            </label>
            <input
              id="reg-email"
              type="email"
              v-model="registerForm.email"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="E-postanızı girin"
            >
          </div>

          <div>
            <label for="reg-password" class="block text-sm font-medium text-gray-700 mb-2">
              Şifre
            </label>
            <input
              id="reg-password"
              type="password"
              v-model="registerForm.password"
              required
              minlength="6"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Şifrenizi girin (en az 6 karakter)"
            >
          </div>

          <button
            type="submit"
            :disabled="authStore.loading"
            class="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span v-if="authStore.loading" class="flex items-center justify-center">
              <i class="fas fa-spinner fa-spin mr-2"></i>
              Hesap oluşturuluyor...
            </span>
            <span v-else>Hesap Oluştur</span>
          </button>
        </form>

        <!-- Error Message -->
        <div v-if="authStore.error" class="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <i class="fas fa-exclamation-circle mr-2"></i>
          {{ authStore.error }}
        </div>

        <!-- Toggle between login/register -->
        <div class="mt-6 text-center">
          <button
            @click="toggleMode"
            class="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            {{ isRegisterMode ? 'Zaten hesabınız var mı? Giriş Yap' : 'Hesabınız yok mu? Kaydol' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/authStore'
import type { LoginRequest, RegisterRequest } from '../types/api'

const router = useRouter()
const authStore = useAuthStore()

const isRegisterMode = ref<boolean>(false)

const loginForm = ref<LoginRequest>({
  email: '',
  password: ''
})

const registerForm = ref<RegisterRequest>({
  name: '',
  email: '',
  password: ''
})

const toggleMode = (): void => {
  isRegisterMode.value = !isRegisterMode.value
  authStore.clearError()
}

const handleLogin = async (): Promise<void> => {
  try {
    await authStore.login(loginForm.value)
    router.push('/dashboard')
  } catch (error) {
    console.error('Login failed:', error)
  }
}

const handleRegister = async (): Promise<void> => {
  try {
    await authStore.register(registerForm.value)
    router.push('/dashboard')
  } catch (error) {
    console.error('Registration failed:', error)
  }
}

onMounted(() => {
  authStore.clearError()
})
</script> 